import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Target, Building2, Users, Zap, ShieldAlert, RefreshCw,
  Globe, TrendingUp, Search, Download, ChevronRight,
  Briefcase, MapPin, DollarSign, Cpu, AlertTriangle,
  CheckCircle2, XCircle, ArrowRight, Loader2, Sparkles, UserPlus,
  Copy, ExternalLink
} from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { ICPTargetingEditor } from '../../components/leads/ICPTargetingEditor';
import { ICPCoachCard } from '../../components/leads/ICPCoachCard';
import { ICPSegmentsBar } from '../../components/leads/ICPSegmentsBar';
import { ICPSegmentCompare } from '../../components/leads/ICPSegmentCompare';
import { ICPRescoreNudge } from '../../components/leads/ICPRescoreNudge';
import { ICPReachHeadline } from '../../components/leads/ICPReachHeadline';
import { ICPTopLeadsPreview } from '../../components/leads/ICPTopLeadsPreview';

const API = process.env.REACT_APP_BACKEND_URL;

// Iter 60 — relative-time helper for "Generated 3h ago" UX (replaces brittle
// `new Date(undefined).toLocaleDateString() → "Invalid Date"`).
function formatRelative(input) {
  if (!input) return null;
  const d = new Date(input);
  if (isNaN(d.getTime())) return null;
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.round(diffH / 24);
  if (diffD < 30) return `${diffD}d ago`;
  return d.toLocaleDateString();
}

export default function ICPDashboard() {
  const navigate = useNavigate();
  const [icp, setIcp] = useState(null);
  const [tenant, setTenant] = useState(null);  // Iter 60 — fallback Company Intel source
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [discoveredLeads, setDiscoveredLeads] = useState([]);
  const [discoveryMeta, setDiscoveryMeta] = useState(null);
  const [importing, setImporting] = useState({});
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [showGenerate, setShowGenerate] = useState(false);
  // Shared refresh signal so the segments bar + compare panel re-fetch after
  // ANY segment mutation (create/edit/delete/activate/promote) — no manual
  // tab reload needed, and the two panels stay in sync with each other.
  const [segTick, setSegTick] = useState(0);

  const loadICP = useCallback(async () => {
    try {
      // Iter 60 — fetch ICP + tenant onboarding status in parallel so we can
      // populate Company Intel from tenant data even when the new flat ICP
      // shape lacks `apollo_data`/`enrichment_data`.
      const [icpRes, statusRes] = await Promise.allSettled([
        api.get(`${API}/api/brain/icp`),
        api.get(`${API}/api/onboarding/status`),
      ]);
      if (icpRes.status === 'fulfilled') setIcp(icpRes.value.data?.icp || null);
      if (statusRes.status === 'fulfilled') setTenant(statusRes.value.data || null);
    } catch (e) {
      console.error('Failed to load ICP:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Called by any segment action — refresh the canonical ICP AND bump the tick
  // so the segments bar + compare panel both re-pull live.
  const onSegmentsChanged = useCallback(async () => {
    await loadICP();
    setSegTick((t) => t + 1);
  }, [loadICP]);

  useEffect(() => { loadICP(); }, [loadICP]);

  // #1 — correct a wrong 'Company Intel' label (e.g. onboarding said SaaS).
  const editCompanyIntel = async () => {
    const current = icp?.company_intel?.industry || '';
    const next = window.prompt('Your company industry (how YOUR business is described):', current);
    if (next == null) return;
    try {
      await api.post(`${API}/api/brain/icp/company-intel`, { industry: next.trim() });
      toast.success('Company Intel updated');
      await loadICP();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update');
    }
  };

  // Company Intel Autofill — suggest the tenant's OWN industry from their live
  // website so they don't have to hand-type it.
  const [intelSuggestion, setIntelSuggestion] = useState(null);
  const [suggestingIntel, setSuggestingIntel] = useState(false);
  const suggestCompanyIntel = async () => {
    setSuggestingIntel(true);
    try {
      const r = await api.get(`${API}/api/brain/icp/company-intel/suggest`);
      const s = r.data?.suggestion;
      if (!s || !s.industry) {
        toast.error(r.data?.error === 'no_website'
          ? 'Add your website first to auto-suggest'
          : 'Could not read your site — try again');
        return;
      }
      setIntelSuggestion(s);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not suggest');
    } finally { setSuggestingIntel(false); }
  };
  const applySuggestedIntel = async () => {
    if (!intelSuggestion?.industry) return;
    try {
      await api.post(`${API}/api/brain/icp/company-intel`, {
        industry: intelSuggestion.industry,
        company_size: intelSuggestion.company_size || undefined,
      });
      toast.success('Company Intel updated from your site');
      setIntelSuggestion(null);
      await loadICP();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not apply');
    }
  };

  // Step 2 — editable ICP scoring weights (what "fit" means to this team).
  const [weightsEdit, setWeightsEdit] = useState(false);
  const [weightDraft, setWeightDraft] = useState({});
  const [savingWeights, setSavingWeights] = useState(false);
  const startEditWeights = () => { setWeightDraft({ ...((icp?.profile || icp)?.scoring_weights || {}) }); setWeightsEdit(true); };
  const saveWeights = async () => {
    setSavingWeights(true);
    try {
      await api.post(`${API}/api/brain/icp/scoring-weights`, { weights: weightDraft });
      toast.success('Scoring weights updated');
      setWeightsEdit(false);
      await loadICP();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save weights');
    } finally { setSavingWeights(false); }
  };

  const handleGenerate = async () => {
    if (!websiteUrl.trim()) { toast.error('Enter a website URL'); return; }
    setGenerating(true);
    try {
      const res = await api.post(`${API}/api/brain/icp/generate`, { website_url: websiteUrl });
      setIcp(res.data.icp);
      setShowGenerate(false);
      setWebsiteUrl('');
      toast.success('ICP generated with data enrichment');
    } catch {
      toast.error('Failed to generate ICP');
    } finally {
      setGenerating(false);
    }
  };

  const handleDiscover = async () => {
    setDiscovering(true);
    try {
      const res = await api.post(`${API}/api/brain/icp/discover-leads?limit=10`);
      setDiscoveredLeads(res.data.leads || []);
      setDiscoveryMeta(res.data);
    } catch {
      toast.error('Failed to discover leads');
    } finally {
      setDiscovering(false);
    }
  };

  const handleImportLead = async (lead) => {
    setImporting(prev => ({ ...prev, [lead.id]: true }));
    try {
      await api.post(`${API}/api/brain/icp/import-leads`, { leads: [lead] });
      toast.success(`${lead.name} imported to CRM`);
      setDiscoveredLeads(prev => prev.filter(l => l.id !== lead.id));
    } catch {
      toast.error('Failed to import lead');
    } finally {
      setImporting(prev => ({ ...prev, [lead.id]: false }));
    }
  };

  const handleImportAll = async () => {
    setImporting(prev => ({ ...prev, all: true }));
    try {
      await api.post(`${API}/api/brain/icp/import-leads`, { leads: discoveredLeads });
      toast.success(`${discoveredLeads.length} leads imported to CRM`);
      setDiscoveredLeads([]);
    } catch {
      toast.error('Failed to import leads');
    } finally {
      setImporting(prev => ({ ...prev, all: false }));
    }
  };

  // Iter 60 — copy ICP summary to clipboard for sales reps to share with team
  const handleCopySummary = async () => {
    const profile = icp?.profile || icp || {};
    const text = profile.summary || '';
    if (!text) { toast.error('No summary to copy'); return; }
    try {
      await navigator.clipboard.writeText(text);
      toast.success('ICP summary copied to clipboard');
    } catch {
      toast.error('Clipboard unavailable');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="icp-loading">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const profile = icp?.profile || icp || {};  // Iter 59b: support Iter 58 flat shape
  const enrichment = icp?.apollo_data || icp?.enrichment_data || {};
  const source = icp?.source || {};
  const firmographic = profile.firmographic || {};
  const technographic = profile.technographic || {};
  const behavioral = profile.behavioral || {};
  const weights = profile.scoring_weights || {};
  // Iter 58 flat shape: top-level `target_personas` → derive decision_makers
  const decisionMakers = profile.decision_makers || profile.target_personas || [];
  const negativeSignals = profile.negative_signals || profile.disqualifiers || [];
  // Iter 60 — robust Company Intel: fall back to tenant onboarding profile
  // when ICP doc lacks apollo_data/enrichment_data (which the new flat
  // shape often does). Also surface industry / website / company size
  // even when Apollo enrichment didn't run.
  const intel = {
    company_name: enrichment.company_name || tenant?.company_name,
    industry: icp?.company_intel?.industry || enrichment.industry || tenant?.industry,
    employee_count: enrichment.employee_count,
    company_size: icp?.company_intel?.company_size || tenant?.company_size,
    annual_revenue: enrichment.annual_revenue,
    linkedin_url: enrichment.linkedin_url,
    website_url: source?.website_url || enrichment.website_url || tenant?.website_url,
  };
  const hasIntel = Boolean(intel.company_name || intel.industry || intel.employee_count || intel.company_size || intel.annual_revenue || intel.website_url);
  const hasWeights = Object.keys(weights).length > 0;
  // Iter 60 — robust generated_at: support flat shape (created_at / updated_at)
  const generatedAt = icp?.generated_at || icp?.updated_at || icp?.created_at;
  const relativeTime = formatRelative(generatedAt);

  return (
    <div className="space-y-6 p-1" data-testid="icp-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
            <Target className="w-6 h-6 text-primary" />
            Ideal Customer Profile
          </h1>
          <p className="text-sm text-muted-foreground mt-1" data-testid="icp-subtitle">
            {icp ? (
              <>
                v{icp.version || 1}
                {' · '}
                {source.apollo_enriched || source.enriched ? 'Lockene Enriched' : 'Brain only'}
                {relativeTime && <> · Generated {relativeTime}</>}
              </>
            ) : 'No ICP generated yet'}
          </p>
        </div>
        <div className="flex gap-2">
          {icp && (
            <button
              data-testid="icp-copy-summary-btn"
              onClick={handleCopySummary}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-border hover:bg-muted/60 transition-colors"
              title="Copy ICP summary"
            >
              <Copy className="w-3.5 h-3.5" /> Copy
            </button>
          )}
          <button
            data-testid="icp-discover-leads-btn"
            onClick={handleDiscover}
            disabled={!icp || discovering}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-[hsl(171,66%,35%)] text-white hover:bg-[hsl(171,66%,30%)] disabled:opacity-50 transition-colors"
          >
            {discovering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Find Leads
          </button>
          {icp ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  data-testid="icp-regenerate-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Regenerate
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="icp-regenerate-confirm">
                <AlertDialogHeader>
                  <AlertDialogTitle>Regenerate your ICP?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will run a fresh Lockene AI analysis on your website plus firmographic
                    data enrichment. The current ICP (v{icp.version || 1}) will be
                    replaced. Estimated cost: <strong>~3 LLM credits + 1 data lookup</strong>.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="icp-regenerate-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => setShowGenerate(true)}
                    data-testid="icp-regenerate-proceed"
                  >
                    Yes, regenerate
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : (
            <button
              data-testid="icp-generate-btn"
              onClick={() => setShowGenerate(true)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Generate ICP
            </button>
          )}
        </div>
      </div>

      {/* Generate Modal */}
      {showGenerate && (
        <div className="rounded-xl border bg-card p-5 shadow-sm" data-testid="icp-generate-form">
          <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" /> Generate ICP from Website
          </h3>
          <p className="text-sm text-muted-foreground mb-3">
            Enter your company website. Lockene will enrich firmographic data, then generate a multi-dimensional ICP.
          </p>
          <div className="flex gap-3">
            <input
              data-testid="icp-website-input"
              type="text"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://yourcompany.com"
              className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              onKeyDown={(e) => e.key === 'Enter' && handleGenerate()}
            />
            <button
              data-testid="icp-generate-submit-btn"
              onClick={handleGenerate}
              disabled={generating}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 disabled:opacity-50"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              {generating ? 'Analyzing...' : 'Generate'}
            </button>
          </div>
        </div>
      )}

      {/* No ICP State */}
      {!icp && !showGenerate && (
        <div className="rounded-xl border-2 border-dashed border-muted p-12 text-center" data-testid="icp-empty-state">
          <Target className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No ICP Generated Yet</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Generate an Ideal Customer Profile to discover and score leads with AI precision.
          </p>
          <button
            onClick={() => setShowGenerate(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90"
          >
            <Sparkles className="w-4 h-4" /> Generate Your ICP
          </button>
        </div>
      )}

      {/* ICP Content */}
      {icp && (
        <>
          {/* Summary + Enrichment Card */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 rounded-xl border bg-card p-5 shadow-sm" data-testid="icp-summary-card">
              <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[hsl(171,66%,35%)]" /> ICP Summary
              </h3>
              <p className="text-sm text-foreground/80 leading-relaxed">{profile.summary}</p>
              {profile.outreach_strategy && (
                <div className="mt-4 p-3 rounded-lg bg-primary/5 border border-primary/10">
                  <p className="text-xs font-medium text-primary mb-1">Recommended Outreach</p>
                  <p className="text-sm text-foreground/70">{profile.outreach_strategy}</p>
                </div>
              )}
            </div>

            {/* Iter 60 — Company Intel: always render when we have any tenant
                or apollo data, with graceful fallbacks. Previously gated on
                `apollo_enriched` which left the card visibly empty. */}
            {hasIntel && (
              <div className="rounded-xl border bg-card p-5 shadow-sm" data-testid="icp-enrichment-card">
                <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Globe className="w-4 h-4 text-primary" /> Company Intel
                </h3>
                <div className="space-y-2.5 text-sm">
                  {intel.company_name && (
                    <div className="flex items-center gap-2" data-testid="intel-company-name">
                      <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="font-medium">{intel.company_name}</span>
                    </div>
                  )}
                  {intel.industry && (
                    <div className="flex items-center gap-2 group" data-testid="intel-industry">
                      <Briefcase className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-muted-foreground">{intel.industry}</span>
                      <button onClick={editCompanyIntel} data-testid="intel-industry-edit"
                        className="text-[10px] text-primary opacity-0 group-hover:opacity-100 hover:underline transition-opacity">
                        edit
                      </button>
                      <button onClick={suggestCompanyIntel} disabled={suggestingIntel}
                        data-testid="intel-industry-suggest"
                        className="inline-flex items-center gap-1 text-[10px] text-primary opacity-0 group-hover:opacity-100 hover:underline transition-opacity disabled:opacity-50">
                        {suggestingIntel ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        suggest from site
                      </button>
                    </div>
                  )}
                  {intelSuggestion?.industry && intelSuggestion.industry !== intel.industry && (
                    <div className="flex items-center gap-2 flex-wrap rounded-lg border border-sky-500/30 bg-sky-500/[0.06] px-2.5 py-2"
                      data-testid="intel-suggestion">
                      <Sparkles className="w-3.5 h-3.5 text-sky-500 shrink-0" />
                      <span className="text-xs text-sky-700 dark:text-sky-300">
                        From your site: <span className="font-semibold">{intelSuggestion.industry}</span>
                        {intelSuggestion.company_size ? ` · ${intelSuggestion.company_size}` : ''}
                      </span>
                      <button onClick={applySuggestedIntel} data-testid="intel-suggestion-apply"
                        className="ml-auto text-[11px] font-medium px-2 py-0.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
                        Apply
                      </button>
                      <button onClick={() => setIntelSuggestion(null)} data-testid="intel-suggestion-dismiss"
                        className="text-[11px] text-muted-foreground hover:underline">Dismiss</button>
                    </div>
                  )}
                  {(intel.employee_count || intel.company_size) && (
                    <div className="flex items-center gap-2" data-testid="intel-employees">
                      <Users className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-muted-foreground">
                        {intel.employee_count
                          ? `${intel.employee_count.toLocaleString()} employees`
                          : `Size: ${intel.company_size}`}
                      </span>
                    </div>
                  )}
                  {intel.annual_revenue && (
                    <div className="flex items-center gap-2" data-testid="intel-revenue">
                      <DollarSign className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-muted-foreground">${intel.annual_revenue} revenue</span>
                    </div>
                  )}
                  {intel.website_url && (
                    <a
                      href={intel.website_url.startsWith('http') ? intel.website_url : `https://${intel.website_url}`}
                      target="_blank" rel="noopener noreferrer"
                      className="text-primary text-xs hover:underline inline-flex items-center gap-1"
                      data-testid="intel-website"
                    >
                      <ExternalLink className="w-3 h-3" /> {intel.website_url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                    </a>
                  )}
                  {intel.linkedin_url && (
                    <a href={intel.linkedin_url} target="_blank" rel="noopener noreferrer"
                       className="text-primary text-xs hover:underline inline-flex items-center gap-1 mt-1"
                       data-testid="intel-linkedin">
                      View on LinkedIn <ArrowRight className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Flag 2 — re-score nudge after a material ICP change */}
          <ICPRescoreNudge icp={icp} />

          {/* #1 Headline live-reach KPI + #3 freshness stamp */}
          <ICPReachHeadline icp={icp} />

          {/* Phase C — Tiered targeting segments */}
          <ICPSegmentsBar icp={icp} refreshKey={segTick} onChanged={onSegmentsChanged} />

          {/* Segment Compare — side-by-side reach, promote the winner */}
          <ICPSegmentCompare refreshKey={segTick} onChanged={onSegmentsChanged} />


          {/* Phase A — Editable targeting + live addressable-pool KPI */}
          <ICPTargetingEditor icp={icp} onSaved={onSegmentsChanged} />

          {/* Phase B — Brain ICP Coach suggestions */}
          <ICPCoachCard icp={icp} onApplied={loadICP} />


          {/* Iter 60 — Scoring Weights: hide entirely when empty (was rendering empty rails) */}
          {hasWeights && (
            <div className="rounded-xl border bg-card p-5 shadow-sm" data-testid="icp-scoring-weights">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-foreground flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-primary" /> ICP Scoring Weights
                </h3>
                {weightsEdit ? (
                  <div className="flex items-center gap-2">
                    <button onClick={saveWeights} disabled={savingWeights} data-testid="icp-weights-save"
                      className="text-xs font-medium px-3 py-1 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50">Save</button>
                    <button onClick={() => setWeightsEdit(false)} data-testid="icp-weights-cancel"
                      className="text-xs px-2 py-1 rounded-lg border border-border hover:bg-muted">Cancel</button>
                  </div>
                ) : (
                  <button onClick={startEditWeights} data-testid="icp-weights-edit"
                    className="text-xs text-primary hover:underline">Edit</button>
                )}
              </div>
              {weightsEdit ? (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="icp-weights-editor">
                  {Object.keys(weights).map((key) => (
                    <div key={key} className="text-center">
                      <input type="number" min="0" max="100" value={weightDraft[key] ?? 0}
                        onChange={(e) => setWeightDraft((d) => ({ ...d, [key]: e.target.value }))}
                        data-testid={`icp-weight-input-${key}`}
                        className="w-full text-center rounded-lg border border-input bg-background px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                      <p className="text-xs text-muted-foreground capitalize mt-1">{key.replace(/_/g, ' ')}</p>
                    </div>
                  ))}
                  <p className="col-span-2 sm:col-span-5 text-[11px] text-muted-foreground">Relative weights — they don't need to total 100.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {Object.entries(weights).map(([key, val]) => (
                    <div key={key} className="text-center">
                      <div className="relative h-2 rounded-full bg-muted overflow-hidden mb-1.5">
                        <div className="absolute inset-y-0 left-0 rounded-full bg-primary" style={{ width: `${val}%` }} />
                      </div>
                      <p className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</p>
                      <p className="text-sm font-semibold text-foreground">{val}%</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 2 — live top-lead preview: tuning weights reorders the ranking */}
          {hasWeights && (
            <ICPTopLeadsPreview
              weights={weightsEdit ? weightDraft : weights}
              editing={weightsEdit}
            />
          )}

          {/* Firmographic + Technographic + Behavioral */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Firmographic */}
            <div className="rounded-xl border bg-card p-5 shadow-sm" data-testid="icp-firmographic">
              <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-primary" /> Firmographic
              </h3>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground font-medium mb-1">Industries</p>
                  <div className="flex flex-wrap gap-1">
                    {(firmographic.industries || []).slice(0, 6).map((ind, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary">{ind}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-medium mb-1">Company Sizes</p>
                  <p className="text-foreground/70">{(firmographic.company_sizes || []).join(', ')}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-medium mb-1">Revenue</p>
                  <p className="text-foreground/70">{(firmographic.revenue_ranges || []).join(', ')}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-medium mb-1">Geography</p>
                  <div className="flex flex-wrap gap-1">
                    {(firmographic.geographies || []).slice(0, 4).map((geo, i) => (
                      <span key={i} className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="w-3 h-3" /> {geo}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Technographic */}
            <div className="rounded-xl border bg-card p-5 shadow-sm" data-testid="icp-technographic">
              <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <Cpu className="w-4 h-4 text-[hsl(171,66%,35%)]" /> Technographic
              </h3>
              <div className="space-y-3 text-sm">
                {(technographic.required_tech || []).length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground font-medium mb-1">Required Tech</p>
                    <div className="flex flex-wrap gap-1">
                      {(technographic.required_tech || []).slice(0, 6).map((t, i) => (
                        <span key={i} className="px-2 py-0.5 rounded-full text-xs bg-[hsl(171,66%,35%)]/10 text-[hsl(171,66%,35%)]">{t}</span>
                      ))}
                    </div>
                  </div>
                )}
                {(technographic.complementary_tools || []).length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground font-medium mb-1">Complementary Tools</p>
                    <div className="flex flex-wrap gap-1">
                      {(technographic.complementary_tools || []).slice(0, 5).map((t, i) => (
                        <span key={i} className="px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground">{t}</span>
                      ))}
                    </div>
                  </div>
                )}
                {technographic.tech_maturity && (
                  <div>
                    <p className="text-xs text-muted-foreground font-medium mb-1">Tech Maturity</p>
                    <p className="text-foreground/70 capitalize">{technographic.tech_maturity}</p>
                  </div>
                )}
                {!(technographic.required_tech || []).length && !(technographic.complementary_tools || []).length && !technographic.tech_maturity && (
                  <p className="text-xs text-muted-foreground italic">Not detected — regenerate with more website content.</p>
                )}
              </div>
            </div>

            {/* Behavioral */}
            <div className="rounded-xl border bg-card p-5 shadow-sm" data-testid="icp-behavioral">
              <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <Zap className="w-4 h-4 text-[hsl(38,92%,50%)]" /> Behavioral Signals
              </h3>
              <div className="space-y-3 text-sm">
                {(behavioral.pain_points || []).length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground font-medium mb-1">Pain Points</p>
                    <ul className="space-y-1">
                      {(behavioral.pain_points || []).slice(0, 4).map((p, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-xs text-foreground/70">
                          <AlertTriangle className="w-3 h-3 mt-0.5 text-[hsl(38,92%,50%)] shrink-0" />
                          <span className="line-clamp-2">{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {(behavioral.buying_triggers || []).length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground font-medium mb-1">Buying Triggers</p>
                    <ul className="space-y-1">
                      {(behavioral.buying_triggers || []).slice(0, 3).map((t, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-xs text-foreground/70">
                          <Zap className="w-3 h-3 mt-0.5 text-primary shrink-0" />
                          <span className="line-clamp-2">{t}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {behavioral.decision_timeline && (
                  <div>
                    <p className="text-xs text-muted-foreground font-medium mb-1">Decision Timeline</p>
                    <p className="text-foreground/70 text-xs">{behavioral.decision_timeline}</p>
                  </div>
                )}
                {!(behavioral.pain_points || []).length && !(behavioral.buying_triggers || []).length && !behavioral.decision_timeline && (
                  <p className="text-xs text-muted-foreground italic">No behavioral signals captured yet.</p>
                )}
              </div>
            </div>
          </div>

          {/* Decision Makers + Negative Signals */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="rounded-xl border bg-card p-5 shadow-sm" data-testid="icp-decision-makers">
              <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <Users className="w-4 h-4 text-primary" /> Target Decision Makers
              </h3>
              <div className="space-y-3">
                {decisionMakers.slice(0, 4).map((dm, i) => (
                  <div key={i} className="p-3 rounded-lg bg-muted/50 border border-muted">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-foreground">{dm.title}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        dm.role_in_deal === 'decision_maker' ? 'bg-primary/10 text-primary' :
                        dm.role_in_deal === 'champion' ? 'bg-[hsl(171,66%,35%)]/10 text-[hsl(171,66%,35%)]' :
                        dm.role_in_deal === 'influencer' ? 'bg-[hsl(38,92%,50%)]/10 text-[hsl(38,92%,50%)]' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {dm.role_in_deal}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">{dm.messaging_angle}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl border bg-card p-5 shadow-sm" data-testid="icp-negative-signals">
              <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-[hsl(4,82%,55%)]" /> Disqualification Signals
              </h3>
              <ul className="space-y-2">
                {negativeSignals.slice(0, 6).map((sig, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-foreground/70">
                    <XCircle className="w-4 h-4 mt-0.5 text-[hsl(4,82%,55%)] shrink-0" />
                    <span className="line-clamp-2">{sig}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Ideal Lead Titles */}
          <div className="rounded-xl border bg-card p-5 shadow-sm" data-testid="icp-ideal-titles">
            <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
              <Target className="w-4 h-4 text-primary" /> Ideal Lead Titles
            </h3>
            <div className="flex flex-wrap gap-2">
              {(profile.ideal_lead_titles || []).map((title, i) => (
                <span key={i} className="px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                  {title}
                </span>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Discovered Leads Section */}
      {discoveredLeads.length > 0 && (
        <div className="rounded-xl border bg-card p-5 shadow-sm" data-testid="icp-discovered-leads">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-[hsl(171,66%,35%)]" />
              Discovered Leads ({discoveredLeads.length})
              {discoveryMeta?.source === 'brain_generated' && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-[hsl(38,92%,50%)]/10 text-[hsl(38,92%,50%)]">
                  AI-Generated
                </span>
              )}
            </h3>
            <button
              data-testid="icp-import-all-btn"
              onClick={handleImportAll}
              disabled={importing.all}
              className="inline-flex items-center gap-2 px-4 py-1.5 text-sm rounded-lg bg-[hsl(171,66%,35%)] text-white hover:bg-[hsl(171,66%,30%)] disabled:opacity-50"
            >
              {importing.all ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              Import All
            </button>
          </div>

          {discoveryMeta?.upgrade_message && (
            <div className="mb-4 p-3 rounded-lg bg-[hsl(38,92%,50%)]/10 border border-[hsl(38,92%,50%)]/20 text-sm text-foreground/70">
              <p className="font-medium text-[hsl(38,92%,50%)] text-xs mb-1">Lockene People Search</p>
              {discoveryMeta.upgrade_message}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground border-b">
                  <th className="text-left py-2 font-medium">Name</th>
                  <th className="text-left py-2 font-medium">Title</th>
                  <th className="text-left py-2 font-medium">Company</th>
                  <th className="text-left py-2 font-medium">Email</th>
                  <th className="text-left py-2 font-medium">Location</th>
                  <th className="text-right py-2 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {discoveredLeads.map((lead) => (
                  <tr key={lead.id} className="border-b border-muted/50 hover:bg-muted/30 transition-colors">
                    <td className="py-2.5 font-medium text-foreground">{lead.name}</td>
                    <td className="py-2.5 text-muted-foreground">{lead.title}</td>
                    <td className="py-2.5 text-muted-foreground">{lead.company}</td>
                    <td className="py-2.5 text-muted-foreground text-xs">{lead.email}</td>
                    <td className="py-2.5 text-muted-foreground text-xs">
                      {[lead.city, lead.country].filter(Boolean).join(', ')}
                    </td>
                    <td className="py-2.5 text-right">
                      <button
                        data-testid={`icp-import-lead-${lead.id}`}
                        onClick={() => handleImportLead(lead)}
                        disabled={importing[lead.id]}
                        className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded-md bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-50"
                      >
                        {importing[lead.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                        Import
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
